import { useMemo } from 'react'
import type {
  ICombinedSimpleModel,
  ISimpleFilterModel
} from 'ag-grid-community'
import { format, isValid } from 'date-fns'

import type {
  IDataTableFilterModel,
  IDataTableFilterType,
  IDataTableFilterUnit
} from '../Tables/DataTable'

const useParseTableFiltersToStringEffect = (
  appliedTableFilters?: IDataTableFilterModel
) => {
  return useMemo(() => {
    let queryString = ''

    const filterTypesComparisonDefaults = {
      blank: {
        number: '= 0',
        date: 'IS null'
      },
      notBlank: {
        number: '<> 0',
        date: 'IS NOT null'
      }
    }

    const getComparisonSymbol = (symbolType: ISimpleFilterModel['type']) => {
      switch (symbolType) {
        case 'equals':
          return '='
        case 'greaterThan':
          return '>'
        case 'lessThan':
          return '<'
        case 'lessThanOrEqual':
          return '<='
        case 'greaterThanOrEqual':
          return '>='
        case 'notEqual':
          return '<>'

        default:
          return ''
      }
    }

    const applyNumericFilters = (
      conditionType: ISimpleFilterModel['type']
    ): ((field: string, fromValue: number, toValue: number) => string) => {
      switch (conditionType) {
        case 'equals': {
          return (field: string, fromValue: number) => {
            const isValueFloat = !!(fromValue % 1)

            if (isValueFloat) {
              //the next floating value is calculated based on how many decimals the fromValue of each
              //particular property has
              const fromValueAmountOfZeros =
                fromValue.toString().split('.')[1].length || 0

              const precision = parseInt(
                `1${'0'.repeat(fromValueAmountOfZeros)}`
              )

              const nextFloatValue = (fromValue * precision + 1) / precision

              return `(${field} >= ${fromValue} AND ${field} < ${nextFloatValue})`
            } else {
              return `${field} = ${fromValue}`
            }
          }
        }

        case 'inRange': {
          return (field: string, fromValue: number, toValue: number) => {
            return `(${field} >= ${fromValue} AND ${field} <= ${toValue})`
          }
        }

        case 'blank':
        case 'notBlank': {
          return (field: string) => {
            return `( ${field} ${filterTypesComparisonDefaults[conditionType]['number']} )`
          }
        }

        default:
          return (field: string, fromValue: number) => {
            return `${field} ${getComparisonSymbol(conditionType)} ${fromValue}`
          }
      }
    }

    // ! these values are bugged. dateFrom and dateTo are not being set correctly
    const applyDateFilters = (
      conditionType: ISimpleFilterModel['type']
    ): ((
      field: string,
      fromDateValue: string,
      toDateValue: string
    ) => string) => {
      switch (conditionType) {
        case 'inRange': {
          return (
            field: string,
            fromDateValue: string,
            toDateValue: string
          ) => {
            if (field === 'FromDate' || field === 'ToDate') {
              return `${field} >= TIMESTAMP '${fromDateValue} 00:00:00' AND ${field} <= TIMESTAMP '${toDateValue} 23:59:59'`
            }

            return `(${field} >= DATE '${fromDateValue}' AND ${field} <= DATE '${toDateValue}')`
          }
        }

        case 'blank':
        case 'notBlank': {
          return (field: string) => {
            return `( ${field} ${filterTypesComparisonDefaults[conditionType]['date']} )`
          }
        }

        case 'equals': {
          return (field: string, fromDateValue: string) => {
            if (field === 'FromDate' || field === 'ToDate') {
              return `${field} >= TIMESTAMP '${fromDateValue} 00:00:00' AND ${field} <= TIMESTAMP '${fromDateValue} 23:59:59'`
            }

            return `${field} = DATE '${fromDateValue}'`
          }
        }

        case 'notEqual': {
          return (field: string, fromDateValue: string) => {
            if (field === 'FromDate' || field === 'ToDate') {
              return `${field} < TIMESTAMP '${fromDateValue} 00:00:00' OR ${field} > TIMESTAMP '${fromDateValue} 23:59:59'`
            }

            return `${field} <> DATE '${fromDateValue}'`
          }
        }

        case 'greaterThan': {
          return (field: string, fromDateValue: string) => {
            return `${field} > TIMESTAMP '${fromDateValue} 23:59:59'`
          }
        }

        default:
          return (field: string, fromDateValue: string) => {
            return `${field} ${getComparisonSymbol(
              conditionType
            )} DATE '${fromDateValue}'`
          }
      }
    }

    const applyFiltersToCondition = (
      field: string,
      condition: IDataTableFilterUnit,
      bounds: { lowerBound: string; upperBound: string }
    ) => {
      const filterTypeToCondition = {
        ['date']: () => {
          const lowerDate = condition[
            bounds.lowerBound as keyof IDataTableFilterUnit
          ] as string

          const upperDate = condition[
            bounds.upperBound as keyof IDataTableFilterUnit
          ] as string

          const fromDate = lowerDate ? new Date(lowerDate) : ''
          const toDate = upperDate ? new Date(upperDate) : ''

          return applyDateFilters(condition.type)(
            field,
            isValid(fromDate) ? format(lowerDate, 'yyyy-MM-dd') : '',
            isValid(toDate) ? format(upperDate, 'yyyy-MM-dd') : ''
          )
        },
        ['number']: () =>
          applyNumericFilters(condition.type)(
            field,
            condition[
              bounds.lowerBound as keyof IDataTableFilterUnit
            ] as number,
            condition[bounds.upperBound as keyof IDataTableFilterUnit] as number
          )
      }

      return filterTypeToCondition[
        condition.filterType as Exclude<IDataTableFilterType, 'set'>
      ]()
    }

    const applyNestedFiltersToCondition = (
      field: string,
      filterTable: ICombinedSimpleModel<IDataTableFilterUnit>,
      bounds: { lowerBound: string; upperBound: string }
    ) => {
      queryString += '('

      queryString +=
        applyFiltersToCondition(field, filterTable.conditions[0], bounds) + ' '
      queryString += filterTable.operator

      queryString +=
        ' ' + applyFiltersToCondition(field, filterTable.conditions[1], bounds)
      queryString += ')'

      return queryString
    }

    const applyFiltersRelatedToAttribute = (
      field: string,
      filterTable: IDataTableFilterUnit,
      bounds: { lowerBound: string; upperBound: string }
    ) => {
      if (Object.keys(filterTable).includes('condition1')) {
        const combinedFilterTable =
          filterTable as ICombinedSimpleModel<IDataTableFilterUnit>

        return applyNestedFiltersToCondition(field, combinedFilterTable, bounds)
      } else {
        return applyFiltersToCondition(field, filterTable, bounds)
      }
    }

    if (appliedTableFilters) {
      const filterKeys = Object.keys(appliedTableFilters)

      for (const fields of filterKeys) {
        const currentIndex = filterKeys.indexOf(fields)
        const filterTable = appliedTableFilters[fields] as IDataTableFilterUnit

        switch (appliedTableFilters[fields].filterType) {
          case 'set':
            if (filterTable.codeValues) {
              queryString += `${fields} IN ('${filterTable.codeValues.join(
                "', '"
              )}')`
            } else if (filterTable.values) {
              const boolishValue: string = filterTable.values[0]
              const normalizedValue = boolishValue.trim().toLowerCase()
              const isTrue = Number(normalizedValue === 'true')

              queryString += `${fields} IN (${isTrue})`
            }

            break
          case 'number':
            queryString += applyFiltersRelatedToAttribute(fields, filterTable, {
              lowerBound: 'filter',
              upperBound: 'filterTo'
            })

            break
          case 'date':
            queryString += applyFiltersRelatedToAttribute(fields, filterTable, {
              lowerBound: 'dateFrom',
              upperBound: 'dateTo'
            })

            break
        }

        if (currentIndex + 1 < filterKeys.length) {
          queryString += ' AND '
        }
      }
    }

    return queryString
  }, [appliedTableFilters])
}

export default useParseTableFiltersToStringEffect
